import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HeaderMain from "../../dasboard-component/Components/HeaderMain";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import ImageModal from "../../common/FullScreenImageModal.jsx";
import VerificationIdModal from "../VerificationIdModal.jsx";
import { MapPin, Image } from "react-feather";
import styles from "./InstituteDashboard.module.css";

const InstituteDashboard = () => {
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const { user } = useAuth();
  const userId = user?.uid;
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [selectedInstitute, setSelectedInstitute] = useState(null);

  useEffect(() => {
    fetchInstitutes();
  }, []);

  const fetchInstitutes = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/institutes`,
        { withCredentials: true }
      );
      setInstitutes(response.data);
    } catch (error) {
      console.error("Error fetching institutes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (instituteId) => {
    navigate(`/institutes/${instituteId}`);
  };

  const handleImageClick = (imageUrl) => {
    setModalImage(imageUrl);
    setIsModalOpen(true);
  };

  const handleRequestAffiliation = (
    instituteId,
    instituteName,
    instituteType
  ) => {
    if (!userId) {
      toast.error("Please login to request affiliation");
      return;
    }

    setSelectedInstitute({
      id: instituteId,
      name: instituteName,
      type: instituteType,
    });
    setIsVerificationModalOpen(true);
  };

  const handleVerificationSubmit = async (verificationId) => {
    if (!selectedInstitute) return;

    setRequesting(true);
    setIsVerificationModalOpen(false);

    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/institute/request-affiliation`,
        {
          institute_id: selectedInstitute.id,
          verification_id: verificationId,
        },
        { withCredentials: true }
      );
      toast.success("Affiliation request sent successfully!");
    } catch (error) {
      console.error("Error requesting affiliation:", error);
      toast.error(error.response?.data?.message || "Error sending request");
    } finally {
      setRequesting(false);
      setSelectedInstitute(null);
    }
  };

  if (loading) {
    return (
      <div>
        <HeaderMain />
        <div className={styles.loadingContainer}>
          <div className={styles.loadingContent}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>Loading institutes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <HeaderMain pageType="instituteDashboard" />
      <div className={styles.contentWrapper}>
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>Sports Institutes</h1>
          <p className={styles.headerSubtitle}>
            Discover and connect with sports institutes
          </p>
          <div className={styles.affiliationsButtonWrapper}>
            <button
              onClick={() => navigate("/user-affiliations")}
              className={styles.affiliationsButton}
            >
              View My Affiliations
            </button>
          </div>
        </div>

        {institutes.length === 0 ? (
          <div className={styles.emptyState}>
            No institutes available at the moment
          </div>
        ) : (
          <div className={styles.grid}>
            {institutes.map((institute) => (
              <div key={institute.id} className={styles.card}>
                <div className={styles.info}>
                  <div className={styles.cardHeader}>
                    <div className={styles.cardImage}>
                      {institute.logo ? (
                        <img
                          src={institute.logo}
                          alt={institute.name}
                          className={styles.image}
                          onClick={() => handleImageClick(institute.logo)}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                            e.target.parentElement.innerHTML = `<div class="${styles.placeholderImage}"><svg width="24" height="24" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" /></svg></div>`;
                          }}
                        />
                      ) : (
                        <div className={styles.placeholderImage}>
                          <Image size={24} />
                        </div>
                      )}
                    </div>
                    <div className={styles.instituteDetails}>
                      <h3 className={styles.instituteName}>{institute.name}</h3>
                      <p className={styles.instituteType}>
                        {institute.institute_type_name}
                      </p>
                      <p className={styles.location}>
                        <MapPin size={12} />
                        {institute.city}, {institute.country}
                      </p>
                      {institute.contact_person && (
                        <p className={styles.contactPerson}>
                          Contact: {institute.contact_person}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className={styles.buttonGroup}>
                    <button
                      onClick={() => handleViewDetails(institute.id)}
                      className={`${styles.button} ${styles.detailsButton}`}
                    >
                      View Details
                    </button>
                    <button
                      onClick={() =>
                        handleRequestAffiliation(
                          institute.id,
                          institute.name,
                          institute.institute_type_name
                        )
                      }
                      disabled={requesting}
                      className={`${styles.button} ${styles.requestButton}`}
                    >
                      {requesting ? "Sending..." : "Request Affiliation"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ImageModal
        images={modalImage}
        initialIndex={0}
        onClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        alt="Institute Logo"
        isBase64={false}
      />

      <VerificationIdModal
        isOpen={isVerificationModalOpen}
        onClose={() => {
          setIsVerificationModalOpen(false);
          setSelectedInstitute(null);
        }}
        onSubmit={handleVerificationSubmit}
        instituteName={selectedInstitute?.name}
        instituteType={selectedInstitute?.type}
      />
    </div>
  );
};

export default InstituteDashboard;
